import React, { useState, useRef, useEffect } from "react";
import Navigation from "@/components/Navigation";
import ScrollProgress from "@/components/ScrollProgress";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Check, Copy, Home, Search, ChevronRight, Mail } from "lucide-react";

import AddressStep   from "@/components/booking/AddressStep";
import CarStep       from "@/components/booking/CarStep";
import ConditionStep from "@/components/booking/ConditionStep";
import ServiceStep   from "@/components/booking/ServiceStep";
import EstimateStep  from "@/components/booking/EstimateStep";
import ContactStep   from "@/components/booking/ContactStep";
import CalendarStep  from "@/components/booking/CalendarStep";
import BookingSummary from "@/components/booking/BookingSummary";
import TrustSection   from "@/components/booking/TrustSection";

/* ─────────────────────────────────────────────────
   STEP CONFIG
───────────────────────────────────────────────── */
const STEPS = [
  { key: "address",   label: "Where",     num: "01" },
  { key: "car",       label: "Vehicle",   num: "02" },
  { key: "condition", label: "Condition", num: "03" },
  { key: "service",   label: "Service",   num: "04" },
  { key: "estimate",  label: "Estimate",  num: "05" },
  { key: "contact",   label: "Contact",   num: "06" },
  { key: "calendar",  label: "When",      num: "07" },
];

const GOLD = "#8A8A8A";
const GOLD_LIGHT = "#AAAAAA";
const GOLD_DARK = "#5A5A5A";

/* ─────────────────────────────────────────────────
   GLASSMORPHISM SUCCESS MODAL
───────────────────────────────────────────────── */
function SuccessModal({ booking, onHome }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(booking.id);
      toast.success("Copied to clipboard");
    } catch (_) {
      toast.error("Long-press to copy.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,0.94)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflowY: "auto",
        padding: "clamp(12px, 3vh, 32px) 16px 24px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1.0, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="glass-card-premium text-center"
        style={{
          width: "min(92vw, 480px)",
          padding: "clamp(20px, 4vw, 36px)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Gold radial glow */}
        <div
          style={{
            position: "absolute",
            top: "-60px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "240px",
            height: "240px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(138,138,138,0.22) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.55, duration: 0.7, type: "spring", stiffness: 180 }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 68,
            height: 68,
            borderRadius: "50%",
            border: "none",
            marginBottom: "20px",
            boxShadow: `inset 0 0 0 2px ${GOLD}, 0 0 60px rgba(138,138,138,0.30)`,
          }}
        >
          <Check size={40} style={{ color: GOLD_LIGHT, strokeWidth: 2 }} />
        </motion.div>

        <p
          className="overline mb-4"
          style={{ color: "rgba(170,170,170,0.85)" }}
        >
          Booking Received
        </p>

        <h2
          style={{
            fontSize: "clamp(1.4rem, 4vw, 2rem)",
            fontWeight: 600,
            letterSpacing: "-0.045em",
            lineHeight: 0.95,
            color: "#f5f5f7",
            marginBottom: "20px",
          }}
        >
          See you on
          <br />
          <span
            style={{
              background: "var(--gold-gradient)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "gold-shimmer 6s linear infinite",
            }}
          >
            {booking?.appointment_date}
          </span>
        </h2>

        <p
          style={{
            color: "rgba(245,245,247,0.55)",
            fontSize: "15px",
            lineHeight: 1.6,
            maxWidth: "400px",
            margin: "0 auto 32px",
            letterSpacing: "-0.01em",
          }}
        >
          Confirmation heading to{" "}
          <span style={{ color: "rgba(245,245,247,0.85)" }}>
            {booking?.contact?.email}
          </span>
          . We'll reach out to confirm shortly.
        </p>

        {/* Booking ref card */}
        <div
          style={{
            background: "rgba(138,138,138,0.07)",
            border: "none",
            borderRadius: "16px",
            padding: "22px 26px",
            marginBottom: "16px",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 12px 32px rgba(0,0,0,0.30)",
          }}
        >
          <p
            className="overline mb-3"
            style={{ color: "rgba(245,245,247,0.4)" }}
          >
            Booking Reference
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <span
              style={{
                fontFamily: "monospace",
                fontSize: "clamp(1.05rem, 3vw, 1.5rem)",
                color: GOLD_LIGHT,
                letterSpacing: "0.05em",
                wordBreak: "break-all",
              }}
              data-testid="success-booking-id"
            >
              {booking?.id}
            </span>
            <button
              onClick={copy}
              style={{
                background: "none",
                border: "none",
                color: "rgba(245,245,247,0.45)",
                cursor: "pointer",
                padding: "6px",
                transition: "color 0.2s",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = GOLD_LIGHT)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(245,245,247,0.45)")}
              data-testid="success-copy-button"
              aria-label="Copy"
            >
              <Copy size={18} />
            </button>
          </div>
          <p
            style={{
              fontSize: "12px",
              color: "rgba(245,245,247,0.4)",
              marginTop: "14px",
              lineHeight: 1.5,
            }}
          >
            Save this. Use it on the{" "}
            <Link to={`/status?id=${booking?.id}`} style={{ color: GOLD_LIGHT }}>
              status page
            </Link>{" "}
            to track, reschedule, or message us.
          </p>
        </div>

        {/* Booking details summary */}
        <div
          style={{
            textAlign: "left",
            background: "rgba(255,255,255,0.025)",
            border: "none",
            borderRadius: "14px",
            padding: "18px 20px",
            marginBottom: "20px",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 24px rgba(0,0,0,0.25)",
          }}
        >
          <p
            className="overline mb-3"
            style={{ color: "rgba(245,245,247,0.4)" }}
          >
            Booking Details
          </p>
          <div style={{ display: "grid", gap: "10px", fontSize: "13px" }}>
            <DetailRow label="Vehicle">
              {[booking?.car?.year, booking?.car?.make, booking?.car?.model].filter(Boolean).join(" ")}
              {booking?.car?.body_type && (
                <span style={{ color: "rgba(245,245,247,0.45)" }}> · {booking.car.body_type}</span>
              )}
            </DetailRow>
            <DetailRow label="Address">
              {booking?.address?.formatted}
            </DetailRow>
            <DetailRow label="When">
              {booking?.appointment_date} at {to12hTime(booking?.appointment_time)}
            </DetailRow>
            <DetailRow label="Estimate">
              <span style={{ color: GOLD_LIGHT, fontWeight: 500, fontFamily: "monospace" }}>
                ${booking?.price?.estimate ?? "--"}
              </span>
            </DetailRow>
          </div>
        </div>

        {/* Status page prompt + email note */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            marginBottom: "28px",
            padding: "12px 16px",
            background: "rgba(138,138,138,0.06)",
            border: "none",
            borderRadius: "999px",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 6px 18px rgba(0,0,0,0.25)",
          }}
        >
          <Mail size={14} style={{ color: GOLD_LIGHT, flexShrink: 0 }} />
          <p
            style={{
              fontSize: "12.5px",
              color: "rgba(245,245,247,0.75)",
              margin: 0,
              lineHeight: 1.45,
              letterSpacing: "-0.005em",
            }}
          >
            A confirmation email is heading to{" "}
            <span style={{ color: "#f5f5f7" }}>{booking?.contact?.email}</span>{" "}
            with your booking details and a direct link to the status page.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          <button onClick={onHome} style={glassBtnSecondary} data-testid="success-home-button">
            <Home size={14} /> Back Home
          </button>
          <Link
            to={`/status?id=${booking?.id}`}
            style={glassBtnPrimary}
            data-testid="success-status-link"
          >
            <Search size={14} /> View Booking
            <ChevronRight size={14} style={{ opacity: 0.7 }} />
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────
   BUTTON STYLES
───────────────────────────────────────────────── */
const glassBtnPrimary = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  background: "var(--gold)",
  color: "#0b0b0b",
  fontFamily: "'Geist', sans-serif",
  fontSize: "13px",
  fontWeight: 500,
  letterSpacing: "-0.01em",
  padding: "11px 24px",
  borderRadius: "999px",
  cursor: "pointer",
  textDecoration: "none",
  transition: "all 0.35s cubic-bezier(0.25,0.46,0.45,0.94)",
  border: "none",
};

const glassBtnSecondary = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  background: "transparent",
  border: "none",
  color: "rgba(245,245,247,0.75)",
  fontFamily: "'Geist', sans-serif",
  fontSize: "13px",
  fontWeight: 500,
  letterSpacing: "-0.01em",
  padding: "11px 24px",
  borderRadius: "999px",
  cursor: "pointer",
  textDecoration: "none",
  transition: "all 0.35s cubic-bezier(0.25,0.46,0.45,0.94)",
};

function DetailRow({ label, children }) {
  return (
    <div className="d-flex" style={{ gap: "12px", alignItems: "baseline" }}>
      <span
        className="overline"
        style={{
          color: "rgba(245,245,247,0.4)",
          width: "78px",
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span style={{ color: "rgba(245,245,247,0.85)", lineHeight: 1.45 }}>
        {children}
      </span>
    </div>
  );
}

function to12hTime(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mStr} ${ampm}`;
}

/* ─────────────────────────────────────────────────
   PROGRESS DOTS
───────────────────────────────────────────────── */
function ProgressDots({ current, total }) {
  return (
    <div className="d-flex align-items-center gap-2" data-testid="booking-progress-dots">
      {Array.from({ length: total }).map((_, i) => (
        <motion.div
          key={i}
          style={{
            height: 6,
            borderRadius: 3,
          }}
          animate={{
            width: i === current ? 28 : 6,
            backgroundColor:
              i < current
                ? "rgba(138,138,138,0.7)"
                : i === current
                ? GOLD
                : "rgba(255,255,255,0.12)",
          }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   FROSTED GLASS BACKGROUND
   (Replaces the removed Three.js car scene)
───────────────────────────────────────────────── */
function FrostedBackdrop() {
  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
      data-testid="frosted-backdrop"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 95% 70% at 50% 0%, #1c1a14 0%, #0b0b0b 55%, #050505 100%)",
        }}
      />
      <motion.div
        className="absolute rounded-full breathe"
        style={{
          width: "60vw",
          height: "60vw",
          top: "-12vw",
          left: "-14vw",
          background: "radial-gradient(circle, rgba(138,138,138,0.10) 0%, transparent 65%)",
          filter: "blur(20px)",
        }}
      />
      <motion.div
        className="absolute rounded-full breathe"
        style={{
          width: "52vw",
          height: "52vw",
          bottom: "-12vw",
          right: "-12vw",
          background: "radial-gradient(circle, rgba(166,134,65,0.10) 0%, transparent 65%)",
          filter: "blur(20px)",
          animationDelay: "3s",
        }}
      />
      <motion.div
        className="absolute rounded-full"
        style={{
          width: "32vw",
          height: "32vw",
          top: "45%",
          left: "62%",
          background: "radial-gradient(circle, rgba(170,170,170,0.06) 0%, transparent 70%)",
          filter: "blur(20px)",
        }}
        animate={{ x: [0, 25, -15, 0], y: [0, -15, 20, 0] }}
        transition={{ duration: 32, repeat: Infinity, ease: "easeInOut" }}
      />
      <div
        className="absolute inset-0"
        style={{
          opacity: 0.035,
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          mixBlendMode: "overlay",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 80% at 50% 50%, transparent 50%, rgba(0,0,0,0.60) 100%)",
        }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────
   MAIN BOOKING PAGE
───────────────────────────────────────────────── */
export default function Booking() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [blackout, setBlackout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    address: null,
    car: null,
    condition: null,
    service: "full",
    notes: "",
    booking: null,
    contact: null,
    appointment_date: null,
    appointment_time: null,
  });

  const formCardRef = useRef(null);

  // Scroll to top of page when step changes
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 80);
  }, [stepIdx]);

  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const startBooking = async (overrides = {}) => {
    setLoading(true);
    try {
      const car = overrides.car ?? data.car;
      const address = overrides.address ?? data.address;
      const condition = overrides.condition ?? data.condition;
      const service = overrides.service ?? data.service;
      const notes = overrides.notes ?? data.notes;
      const carPayload = car ? { ...car } : null;
      if (carPayload) {
        const y = parseInt(carPayload.year, 10);
        carPayload.year = Number.isFinite(y) ? y : null;
      }
      const { data: created } = await api.post("/bookings/start", {
        address, car: carPayload, condition, service, notes,
      });
      setData((d) => ({ ...d, booking: created }));
      next();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't start booking"));
    } finally {
      setLoading(false);
    }
  };

  const confirmBooking = async (date, time) => {
    setLoading(true);
    try {
      const appointment_date = date ?? data.appointment_date;
      const appointment_time = time ?? data.appointment_time;
      const { data: confirmed } = await api.post("/bookings/confirm", {
        booking_id: data.booking.id,
        contact: data.contact,
        appointment_date,
        appointment_time,
      });
      setData((d) => ({ ...d, booking: confirmed, appointment_date, appointment_time }));
      handleBlackout();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't confirm booking"));
    } finally {
      setLoading(false);
    }
  };

  const handleBlackout = () => {
    setBlackout(true);
    setTimeout(() => setShowSuccess(true), 500);
  };

  const step = STEPS[stepIdx]?.key;

  return (
    <div
      style={{ minHeight: "100vh", background: "#0b0b0b", position: "relative" }}
      data-testid="booking-page"
    >
      <ScrollProgress />
      <FrostedBackdrop />

      {/* Bottom vignette for legibility */}
      <div
        className="fixed inset-x-0 bottom-0 pointer-events-none"
        style={{
          height: "30vh",
          zIndex: 1,
          background: "linear-gradient(to top, rgba(0,0,0,0.60), transparent)",
        }}
      />

      {/* Blackout overlay (pre-modal) */}
      {blackout && (
        <motion.div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 150,
            background: "#000",
            pointerEvents: "none",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        />
      )}

      {/* Success Modal */}
      {showSuccess && data.booking && (
        <SuccessModal
          booking={data.booking}
          onHome={() => navigate("/")}
        />
      )}

      {/* Navigation */}
      <Navigation />

      {/* Hero header for the booking page */}
      <div
        className="relative text-center"
        style={{ zIndex: 5, paddingTop: "120px", paddingBottom: "24px" }}
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="overline mb-4"
          style={{ color: "rgba(170,170,170,0.85)" }}
        >
          Book a Detail
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-semibold tracking-[-0.045em] leading-[0.95] mx-auto px-5"
          style={{
            fontSize: "clamp(2rem, 6vw, 4.2rem)",
            color: "#f5f5f7",
            maxWidth: "780px",
          }}
        >
          Reserve your{" "}
          <em
            className="italic"
            style={{
              fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif",
              background: "var(--gold-gradient)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "gold-shimmer 6s linear infinite",
            }}
          >
            detail
          </em>
          .
        </motion.h1>
      </div>

      {/* Form Card + Summary layout */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          padding: "16px 20px 64px",
        }}
      >
        <div className="container" style={{ maxWidth: 1200 }}>
          <div className="row g-4 g-lg-5 align-items-start booking-layout-grid">
            {/* Form card column */}
            <div className="col-12 col-lg-7 col-xl-7">
              <div className="d-flex align-items-start justify-content-center">
                <motion.div
                  ref={formCardRef}
                  layout
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="glass-card-premium w-100"
                  style={{
                    maxWidth: step === "calendar" ? 880 : 580,
                    padding: "clamp(24px, 4vw, 44px)",
                    position: "relative",
                  }}
                >
                  {/* Gold top glow */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-60px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "260px",
                      height: "160px",
                      borderRadius: "50%",
                      background: "radial-gradient(circle, rgba(138,138,138,0.12) 0%, transparent 70%)",
                      pointerEvents: "none",
                    }}
                  />

                  {/* Progress */}
                  <div
                    className="mb-8 mb-md-10"
                    data-testid="booking-progress"
                  >
                    <div className="d-flex align-items-center justify-content-between mb-4 mb-md-5">
                      <div className="d-flex align-items-baseline gap-3">
                        <span
                          className="font-mono"
                          style={{ color: "rgba(170,170,170,0.85)", fontSize: 11, letterSpacing: "0.28em" }}
                        >
                          {STEPS[stepIdx]?.num}
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: "rgba(245,245,247,0.6)", fontSize: 14, letterSpacing: "-0.01em" }}
                        >
                          {STEPS[stepIdx]?.label}
                        </span>
                      </div>
                      <ProgressDots current={stepIdx} total={STEPS.length} />
                    </div>
                    {/* Progress bar */}
                    <div
                      style={{
                        height: 2,
                        background: "rgba(255,255,255,0.06)",
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 2,
                      }}
                    >
                      <motion.div
                        style={{
                          position: "absolute",
                          inset: "0 auto 0 0",
                          background: "var(--gold-gradient)",
                          backgroundSize: "200% auto",
                          animation: "gold-shimmer 6s linear infinite",
                          borderRadius: 2,
                        }}
                        animate={{ width: `${(stepIdx / (STEPS.length - 1)) * 100}%` }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                      />
                    </div>
                  </div>

                  {/* Step content */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, y: 20, scale: 0.985 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -14, scale: 0.985 }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    >
                      {step === "address" && (
                        <AddressStep
                          value={data.address}
                          onChange={(v) => setData((d) => ({ ...d, address: v }))}
                          onNext={next}
                        />
                      )}
                      {step === "car" && (
                        <CarStep
                          value={data.car}
                          onChange={(v) => setData((d) => ({ ...d, car: v }))}
                          onNext={next}
                          onBack={back}
                        />
                      )}
                      {step === "condition" && (
                        <ConditionStep
                          value={data.condition}
                          notes={data.notes}
                          onChange={(v, notes) =>
                            setData((d) => ({ ...d, condition: v, notes }))
                          }
                          onNext={(v, notes) => {
                            setData((d) => ({ ...d, condition: v, notes }));
                            next();
                          }}
                          onBack={back}
                        />
                      )}
                      {step === "service" && (
                        <ServiceStep
                          value={data.service}
                          onChange={(v) => setData((d) => ({ ...d, service: v }))}
                          onNext={(selected) => startBooking({ service: selected })}
                          onBack={back}
                        />
                      )}
                      {step === "estimate" && (
                        <EstimateStep
                          booking={data.booking}
                          onNext={next}
                          onBack={back}
                        />
                      )}
                      {step === "contact" && (
                        <ContactStep
                          value={data.contact}
                          onChange={(v) => setData((d) => ({ ...d, contact: v }))}
                          onNext={next}
                          onBack={back}
                        />
                      )}
                      {step === "calendar" && (
                        <CalendarStep
                          date={data.appointment_date}
                          time={data.appointment_time}
                          onChange={(date, time) =>
                            setData((d) => ({
                              ...d,
                              appointment_date: date,
                              appointment_time: time,
                            }))
                          }
                          onConfirm={confirmBooking}
                          onBack={back}
                          loading={loading}
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>
                </motion.div>
              </div>
            </div>

            {/* Summary side panel — hidden on mobile, only shown on lg+ */}
            <div className="d-none d-lg-block col-lg-5 col-xl-5 booking-side-col">
              <div
                className="position-sticky"
                style={{ top: 96 }}
              >
                <BookingSummary data={data} currentStep={step} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust band */}
      <TrustSection />

      {/* Footer links */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          textAlign: "center",
          paddingBottom: "32px",
        }}
      >
        <p
          style={{
            fontSize: "12px",
            color: "rgba(245,245,247,0.25)",
            letterSpacing: "0.02em",
          }}
        >
          <Link
            to="/privacy"
            style={{ color: "rgba(245,245,247,0.35)", marginRight: "20px" }}
            className="hover:text-gold transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            style={{ color: "rgba(245,245,247,0.35)" }}
            className="hover:text-gold transition-colors"
          >
            Terms of Service
          </Link>
        </p>
      </div>
    </div>
  );
}
